"use client";

import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input, Button } from "@/shared/presentation/components/ui";
import { Label } from "@/shared/presentation/components/ui";
import { cn } from "@/shared/core/utils";

export interface FormInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  error?: string | null;
  isShowingError?: boolean;
  leadingIcon?: React.ReactNode;
  type?: React.HTMLInputTypeAttribute;
  labelClassName?: string;
  inputClassName?: string;
  errorClassName?: string;
}

/**
 * Form input component with label, error display, and password toggle
 * Replicates the old project's AppInput component with shadcn styling
 */
const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      isShowingError = false,
      leadingIcon,
      type = "text",
      className,
      labelClassName,
      inputClassName,
      errorClassName,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === "password";
    const inputId = id || React.useId();

    const inputType = isPassword ? (showPassword ? "text" : "password") : type;

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <Label
          htmlFor={inputId}
          className={cn(
            "text-sm font-semibold tracking-wide text-primary-dark",
            labelClassName
          )}
        >
          {label}
        </Label>
        <div
          className={cn(
            "relative flex items-center rounded-md border-2 border-primary/30 transition-colors",
            "focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20",
            {
              "border-destructive focus-within:border-destructive focus-within:ring-destructive/20":
                isShowingError && error,
              "bg-muted": disabled,
            }
          )}
        >
          {leadingIcon && (
            <div className="flex h-full items-center pl-3 text-muted-foreground">
              {leadingIcon}
            </div>
          )}
          <Input
            ref={ref}
            id={inputId}
            type={inputType}
            disabled={disabled}
            className={cn(
              "border-0 bg-transparent shadow-none focus-visible:ring-0",
              {
                "pr-10": isPassword,
                "pl-2": leadingIcon,
              },
              inputClassName
            )}
            {...props}
          />
          {isPassword && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        {isShowingError && error && (
          <p
            className={cn("text-sm text-destructive", errorClassName)}
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = "FormInput";

export { FormInput };
export default FormInput;
